import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { authApi } from '../lib/api';
import { Rocket, AlertCircle, Zap, Building2, Mail, Lock, CheckCircle } from 'lucide-react';

export function InitPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setAuth } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Hasła nie są identyczne');
      return;
    }

    if (password.length < 8) {
      setError('Hasło musi mieć minimum 8 znaków');
      return;
    }

    setLoading(true);

    try {
      const { data } = await authApi.init({ email, password, tenantName });
      setAuth(data.token, data.user, data.tenant);
      navigate('/');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Wystąpił błąd inicjalizacji');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { text: 'Podaj nazwę firmy', completed: tenantName.length > 0 },
    { text: 'Wprowadź email administratora', completed: email.includes('@') },
    { text: 'Ustaw bezpieczne hasło', completed: password.length >= 8 },
    { text: 'Potwierdź hasło', completed: confirmPassword === password && confirmPassword.length >= 8 },
  ];

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden py-12 px-4">
      {/* Animated background orbs */}
      <div className="orb-purple w-96 h-96 -top-48 -right-48" />
      <div className="orb-cyan w-80 h-80 bottom-1/4 -left-40" style={{ animationDelay: '2s' }} />
      <div className="orb-pink w-64 h-64 -bottom-32 right-1/4" style={{ animationDelay: '4s' }} />

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `linear-gradient(rgba(191,0,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(191,0,255,0.1) 1px, transparent 1px)`,
          backgroundSize: '50px 50px'
        }}
      />

      <div className="max-w-md w-full relative z-10">
        {/* Logo */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-accent-secondary to-accent-tertiary mb-4 shadow-neon-purple">
            <Zap className="w-8 h-8 text-text-primary" />
          </div>
          <h1 className="text-4xl font-bold text-gradient">Ecomflow</h1>
          <p className="mt-2 text-text-secondary">Konfiguracja początkowa systemu</p>
        </div>

        {/* Progress steps */}
        <div className="card mb-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
          <h3 className="text-sm font-medium text-text-secondary mb-4">Postęp konfiguracji</h3>
          <div className="space-y-3">
            {steps.map((step, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${
                  step.completed
                    ? 'bg-gradient-to-r from-success to-accent-primary shadow-neon-green/50'
                    : 'bg-surface-hover border border-border'
                }`}>
                  {step.completed ? (
                    <CheckCircle className="w-4 h-4 text-dark-900" />
                  ) : (
                    <span className="text-xs text-text-muted">{index + 1}</span>
                  )}
                </div>
                <span className={`text-sm transition-colors ${step.completed ? 'text-success' : 'text-text-muted'}`}>
                  {step.text}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Init card */}
        <div className="card animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="flex items-center p-4 bg-danger/10 border border-danger/30 rounded-xl text-danger animate-scale-in">
                <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div>
              <label htmlFor="tenantName" className="label">
                <Building2 className="w-4 h-4 inline mr-2" />
                Nazwa firmy
              </label>
              <input
                id="tenantName"
                type="text"
                required
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                className="input"
                placeholder="Moja Firma Sp. z o.o."
              />
            </div>

            <div>
              <label htmlFor="email" className="label">
                <Mail className="w-4 h-4 inline mr-2" />
                Email administratora
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="admin@firma.pl"
              />
            </div>

            <div>
              <label htmlFor="password" className="label">
                <Lock className="w-4 h-4 inline mr-2" />
                Hasło
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="Minimum 8 znaków"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="label">
                <Lock className="w-4 h-4 inline mr-2" />
                Potwierdź hasło
              </label>
              <input
                id="confirmPassword"
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input"
                placeholder="Powtórz hasło"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="loader w-5 h-5 mr-2" />
                  Inicjalizacja...
                </span>
              ) : (
                <span className="flex items-center justify-center">
                  <Rocket className="w-5 h-5 mr-2" />
                  Uruchom system
                </span>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-border-subtle text-center">
            <Link
              to="/login"
              className="text-sm text-text-secondary hover:text-accent-secondary transition-colors"
            >
              Masz już konto? Zaloguj się
            </Link>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-8 text-center text-xs text-text-disabled">
          Ecomflow &copy; 2026 - Platforma e-commerce
        </p>
      </div>
    </div>
  );
}
